import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Label, makeConfig, PaginatedTable2 } from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { TIME_FORMAT } from '@pflegenavi/shared/constants';
import type { CashTransactionRow } from './CashTransactionRowActions';
import { CashTransactionRowActions } from './CashTransactionRowActions';
import { useLinkedTablePagination } from './useLinkedTablePagination';

type ColumnNames =
  | 'type'
  | 'employee'
  | 'date'
  | 'amount'
  | 'fees'
  | 'notes'
  | 'actions';

const NotesCell: FC<{ notes: string }> = ({ notes }) => (
  <Tooltip arrow placement="top-start" title={notes}>
    <Typography
      variant="body2"
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        maxWidth: 320,
      }}
    >
      {notes || '--'}
    </Typography>
  </Tooltip>
);

export const CashTransactionsTable: FC<{
  transactions: CashTransactionRow[];
  showDownloadPdf: (transaction: CashTransactionRow) => boolean;
}> = ({ transactions, showDownloadPdf }) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();

  const { page, pageSize, pagination } = useLinkedTablePagination(
    transactions.length
  );
  const rows = useMemo(
    () => transactions.slice(page * pageSize, (page + 1) * pageSize),
    [transactions, page, pageSize]
  );

  const hasFees = transactions.some(
    ({ immediateFeeAmount, separateFeeAmount }) =>
      immediateFeeAmount !== 0 || separateFeeAmount !== 0
  );

  const config = useMemo(
    () =>
      makeConfig<CashTransactionRow, ColumnNames, never>({
        showAll: undefined,
        hideEmptyRows: true,
        columns: [
          {
            key: 'type',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.type'
            ),
            align: 'left',
            component: ({ row }) => (
              <Label color={row.amount >= 0 ? 'success' : 'error'}>
                {row.amount >= 0
                  ? t('cashManagement.deposit')
                  : t('cashManagement.withdrawal')}
              </Label>
            ),
          },
          {
            key: 'employee',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.employee'
            ),
            align: 'left',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {row.employee
                  ? `${row.employee.firstName ?? ''} ${
                      row.employee.lastName ?? ''
                    }`.trim()
                  : '--'}
              </Typography>
            ),
          },
          {
            key: 'date',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.date'
            ),
            align: 'left',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {fDate(row.date, TIME_FORMAT)}
              </Typography>
            ),
          },
          {
            key: 'amount',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.amount'
            ),
            align: 'right',
            component: ({ row }) => (
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ color: row.amount >= 0 ? 'success.main' : 'error.main' }}
              >
                {fCurrencyCents(row.amount)}
              </Typography>
            ),
          },
          ...(hasFees
            ? [
                {
                  key: 'fees' as const,
                  label: t(
                    'cashManagement.group-detail.cash-transactions-table.fees'
                  ),
                  align: 'right' as const,
                  component: ({ row }: { row: CashTransactionRow }) => (
                    <Stack
                      direction="row"
                      sx={{ gap: 0.5, justifyContent: 'flex-end' }}
                    >
                      {row.immediateFeeAmount !== 0 && (
                        <Tooltip
                          arrow
                          title={t(
                            'cashManagement.group-detail.immediate-fee-amount'
                          )}
                        >
                          <Label color="default">
                            {fCurrencyCents(row.immediateFeeAmount)}
                          </Label>
                        </Tooltip>
                      )}
                      {row.separateFeeAmount !== 0 && (
                        <Tooltip
                          arrow
                          title={t(
                            'cashManagement.group-detail.separate-fee-amount'
                          )}
                        >
                          <Label color="warning">
                            {fCurrencyCents(row.separateFeeAmount)}
                          </Label>
                        </Tooltip>
                      )}
                    </Stack>
                  ),
                },
              ]
            : []),
          {
            key: 'notes',
            label: t(
              'cashManagement.group-detail.cash-transactions-table.notes'
            ),
            align: 'left',
            component: ({ row }) => <NotesCell notes={row.notes} />,
          },
          {
            key: 'actions',
            label: '',
            align: 'right',
            component: ({ row }) => (
              <CashTransactionRowActions
                transaction={row}
                showDownloadPdf={showDownloadPdf(row)}
              />
            ),
          },
        ],
        pagination,
      }),
    [t, fDate, fCurrencyCents, hasFees, pagination, showDownloadPdf]
  );

  return (
    <PaginatedTable2
      name="CashTransactionGroupDetailCashTransactionTable"
      config={config}
      rows={rows}
      isLoading={false}
      embedded
    />
  );
};
